/**
 * AS CONTAS DA CATIRA (D3) — o estoque do juntador de gado, por contagem e
 * custo médio. Sem tela e sem armazenamento, para dar para testar
 * (`testes/contas.mjs`). Os tipos moram aqui pelo mesmo motivo; o armazém da
 * catira (`src/dados/catira.ts`) os reexporta. Na tela o nome é «Meu estoque».
 *
 * O juntador controla CABEÇAS E DINHEIRO, não animais: compra 20, 30 e 50 e
 * vende 100 em lotes fracionados, e nada liga a venda à compra de origem.
 *
 * O saldo nunca é guardado: é recalculado aqui, dos movimentos válidos, em
 * ordem de data. Assim um movimento lançado sem sinal e sincronizado depois
 * entra no lugar certo, e anular um movimento é só deixá-lo de fora.
 *
 *   entrou gado        cabeças +   custo + (custo total da compra)
 *   recria             —           custo +
 *   morte              cabeças −   custo fica (espalha nas que ficaram)
 *   venda              cabeças −   custo sai pelo CUSTO MÉDIO × cabeças
 *   contagem           cabeças = o que contou; a diferença vira sumidos
 *                      (perda) ou encontradas (ganho), pelo custo médio
 *
 *   lucro da venda = valor da venda − custo médio × cabeças vendidas
 *
 * R2D · POR CATEGORIA (decisão do gestor, 06/10/2026: «discriminar por
 * categorias e preços estratificados mantém o rastreamento assertivo»). Cada
 * categoria do gado (bezerro, vaca, boi…) é um estoque com as suas cabeças, o
 * seu custo médio e o seu resultado — a conta acima, igualzinha, dentro de
 * cada uma. A venda de bezerros sai pelo custo médio DOS BEZERROS. Movimento
 * antigo (sem categoria) fica em «Geral»: quem trabalha com uma categoria só
 * continua vendo um estoque só, e nenhum número antigo muda.
 *
 *   recria de UMA categoria  → todo o custo nela
 *   recria «rateada»         → repartida PELAS CABEÇAS de cada categoria na
 *                              data (ao centavo, pelo maior resto). Sem gado
 *                              nenhum na data, o custo espera e entra na
 *                              próxima compra (como sempre foi)
 *   morte, contagem          → sempre de uma categoria
 *
 * O RESULTADO soma o lucro das vendas e, numa linha só, o «ajuste de
 * contagem» (encontradas − sumidos). A perda por morte aparece com o custo
 * médio da data, para o gestor ver quanto as mortes custaram — mas esse custo
 * já está espalhado nas que ficaram e não se desconta de novo (só a morte da
 * ÚLTIMA cabeça de uma categoria, que não tem em quem espalhar, desconta).
 * O «custo das entradas» (compras + recria) é informativo: não é caixa.
 *
 * Toda conta em centavos inteiros: o custo que sai numa venda é
 * custo × cabeças ÷ estoque, arredondado; vendendo tudo, sai o custo inteiro
 * (sem sobrar centavo fantasma).
 */
import { calcular, fechamentoDaCompra, ratear } from './contas'
import { CATEGORIA_GERAL, estoqueDaFicha, estoquePorCategoria, fichaPorCategoria, type ParteDaCategoria } from './destinos'
import { dataBR } from './numeros'
import type { Celula, Aba } from './planilha'
import type { Comissao, Ficha } from './tipos'

export type { ParteDaCategoria } from './destinos'
export { CATEGORIA_GERAL } from './destinos'

export type TipoDeMovimento = 'entrada' | 'ajuste_entrada' | 'recria' | 'morte' | 'venda' | 'ajuste_venda' | 'contagem'

export const ESTOQUE_PRINCIPAL = 'principal'

/** R2D · recria «rateada por cabeça» entre as categorias que têm gado na data */
export const RATEIO = '*'

export const NOME_DO_TIPO: Record<TipoDeMovimento, string> = {
  entrada: 'Entrada (compra)',
  ajuste_entrada: 'Ajuste de compra',
  recria: 'Recria',
  morte: 'Morte',
  venda: 'Venda',
  ajuste_venda: 'Ajuste de venda',
  contagem: 'Contagem no pasto',
}

export const CATEGORIAS_DE_RECRIA = ['Pasto', 'Sal', 'Ração', 'Vacina', 'Mão de obra', 'Outro']

export interface AnulacaoDaCatira { em: string; por?: string; motivo: string }

export interface Movimento {
  id: string
  estoqueId: string
  tipo: TipoDeMovimento
  /** o dia do acontecimento: é a ordem do recálculo */
  data: string
  /** hora do lançamento no aparelho: desempate no mesmo dia */
  em: string
  /** efeito nas cabeças: + entra, − sai. Na contagem, a diferença que se viu no dia (informativo) */
  cabecas: number
  /** só contagem: quantas a contagem achou — é este número que o recálculo usa */
  contadas: number | null
  /** custo (entrada, ajuste de compra, recria) ou o que a venda rendeu (venda, ajuste de venda) */
  valor: number
  /** o TIPO DO GASTO da recria (pasto, sal…). Não é a categoria do gado: essa é `categoriaGado`. */
  categoria: string
  descricao: string
  fichaId: string | null
  lancadoPor: string
  lancadoPorNome: string
  lancadoEm: string
  anulacao: AnulacaoDaCatira | null
  /** o servidor recusou de vez: só existe neste aparelho, riscado e fora das contas */
  recusa?: { em: string; motivo: string } | null
  /**
   * R2D · a categoria do gado (Bezerro, Vaca…). Ausente (movimento de antes
   * da 015): «Geral». Na recria, `RATEIO` = rateada pelas cabeças.
   */
  categoriaGado?: string
  /** R2D · peso da balança e arrobas (entrada, venda e ajustes). Nulo: não se sabe (algum animal sem peso). */
  kg?: number | null
  arrobas?: number | null
}

/** A categoria do gado de um movimento: a dele, ou «Geral» (movimento antigo). */
export function categoriaDoMovimento(m: Pick<Movimento, 'categoriaGado'>): string {
  return String(m.categoriaGado ?? '').trim() || CATEGORIA_GERAL
}

/** O que um movimento fez com o estoque, depois de recalculado em ordem. */
export interface Efeito {
  m: Movimento
  /** R2D · a categoria em que o movimento mexeu (`RATEIO` na recria rateada) */
  categoria: string
  /** cabeças e custo DA CATEGORIA antes e depois (na recria rateada, do estoque inteiro) */
  cabAntes: number
  cabDepois: number
  custoAntes: number
  custoDepois: number
  /** custo que saiu do estoque (venda, sumidos, zeragem, morte da última cabeça) */
  custoSaiu: number
  /** custo que entrou (compra, recria, encontradas) */
  custoEntrou: number
  /** venda e ajuste de venda: valor − custo que saiu */
  lucro: number
  /** contagem: valor das cabeças encontradas além dos sumidos de antes */
  ganho: number
  /** contagem: valor dos sumidos (e da zeragem) */
  perda: number
  /** contagem: encontradas que anulam sumidos de antes (valor que volta) */
  recuperado: number
  /** morte da última cabeça: o custo que ficou sem gado para carregar vira perda */
  perdaMorte: number
  /**
   * R2D · morte: quanto as cabeças mortas valiam ao custo médio da data. É
   * para o gestor VER o que a morte custou — o custo continua espalhado nas
   * que ficaram (não sai do estoque, salvo a última cabeça: `perdaMorte`).
   */
  custoDaMorte: number
  /** R2D · recria rateada: quanto foi para cada categoria (vazio: não havia gado, o custo espera a próxima compra) */
  rateio?: { categoria: string; cabecas: number; valor: number }[]
  /**
   * Saíram mais cabeças do que havia (venda ou morte lançada sem a contagem
   * antes — ex.: dois celulares, ou um lançamento atrasado). O estoque não
   * fica negativo: essas cabeças entram como encontradas pelo custo médio, e
   * a tela avisa para conferir.
   */
  faltaram: number
}

export interface Saldo {
  cabecas: number
  custo: number
  /** nulo sem cabeças: não há média de nada */
  custoMedio: number | null
  /** sumidos que ainda não reapareceram */
  sumidos: number
}

export interface SaldoDaCategoria extends Saldo {
  categoria: string
}

export interface Calculo extends Saldo {
  efeitos: Efeito[]
  /** R2D · o saldo de cada categoria, na ordem em que apareceram */
  categorias: SaldoDaCategoria[]
  /** R2D · recria rateada lançada quando não havia gado nenhum: entra na próxima compra */
  semGado: number
}

const c = (n: number) => Math.round(n * 100)
const r = (centavos: number) => centavos / 100 || 0

/** Vale = não anulado, não recusado. Ajuste de ficha sem a entrada (ou venda) valendo não conta. */
export function validos(movs: Movimento[], estoqueId = ESTOQUE_PRINCIPAL): Movimento[] {
  const base = movs.filter((m) => m.estoqueId === estoqueId && !m.anulacao && !m.recusa)
  const comEntrada = new Set(base.filter((m) => m.tipo === 'entrada').map((m) => m.fichaId))
  const comVenda = new Set(base.filter((m) => m.tipo === 'venda').map((m) => m.fichaId))
  return base.filter((m) => (m.tipo !== 'ajuste_entrada' || comEntrada.has(m.fichaId))
    && (m.tipo !== 'ajuste_venda' || comVenda.has(m.fichaId)))
}

/** A ordem do recálculo: dia, hora do lançamento, id. */
export function emOrdem(movs: Movimento[]): Movimento[] {
  return [...movs].sort((a, b) => a.data.localeCompare(b.data) || a.em.localeCompare(b.em) || a.id.localeCompare(b.id))
}

interface Bolso { cab: number; custo: number; sumidos: number }

/**
 * Recalcula o estoque inteiro: passa pelos movimentos válidos em ordem e diz,
 * para cada um, o que ele fez. O saldo final é o estoque de hoje — por
 * categoria e somado.
 */
export function recalcular(movs: Movimento[], estoqueId = ESTOQUE_PRINCIPAL): Calculo {
  const bolsos = new Map<string, Bolso>()
  const bolso = (k: string): Bolso => {
    let b = bolsos.get(k)
    if (!b) { b = { cab: 0, custo: 0, sumidos: 0 }; bolsos.set(k, b) }
    return b
  }
  let semGado = 0 // centavos de recria rateada sem gado nenhum
  const totalCab = () => [...bolsos.values()].reduce((s, b) => s + b.cab, 0)
  const totalCusto = () => [...bolsos.values()].reduce((s, b) => s + b.custo, 0) + semGado
  /** custo médio com que cada venda saiu (por ficha e categoria): ajuste que devolve cabeças volta por ele */
  const medioDaVenda = new Map<string, number>()
  const efeitos: Efeito[] = []

  for (const m of emOrdem(validos(movs, estoqueId))) {
    const categoria = categoriaDoMovimento(m)

    // R2D · recria rateada: pelas cabeças de cada categoria na data
    if (m.tipo === 'recria' && categoria === RATEIO) {
      const cabAntes = totalCab(), custoAntes = totalCusto()
      const v = c(m.valor)
      const comGado = [...bolsos.entries()].filter(([, b]) => b.cab > 0)
      let rateio: NonNullable<Efeito['rateio']> = []
      if (!comGado.length) semGado += v
      else {
        const partes = ratear(v, comGado.map(([, b]) => b.cab))
        comGado.forEach(([, b], i) => { b.custo += partes[i]! })
        rateio = comGado.map(([k, b], i) => ({ categoria: k, cabecas: b.cab, valor: r(partes[i]!) }))
      }
      efeitos.push({
        m, categoria: RATEIO, cabAntes, cabDepois: cabAntes, custoAntes: r(custoAntes), custoDepois: r(totalCusto()),
        custoSaiu: 0, custoEntrou: r(v), lucro: 0, ganho: 0, perda: 0, recuperado: 0, perdaMorte: 0, custoDaMorte: 0, faltaram: 0, rateio,
      })
      continue
    }

    const b = bolso(categoria)
    let cab = b.cab
    let custo = b.custo // centavos
    let sumidos = b.sumidos
    /** custo de n cabeças pelo custo médio de agora; tirando todas, sai o custo inteiro */
    const parte = (n: number) => (cab <= 0 ? 0 : n >= cab ? custo : Math.round((custo * n) / cab))

    const e: Efeito = {
      m, categoria, cabAntes: cab, cabDepois: cab, custoAntes: r(custo), custoDepois: r(custo),
      custoSaiu: 0, custoEntrou: 0, lucro: 0, ganho: 0, perda: 0, recuperado: 0, perdaMorte: 0, custoDaMorte: 0, faltaram: 0,
    }
    let saiu = 0, entrou = 0, ganho = 0, perda = 0, recuperado = 0, perdaMorte = 0, lucro = 0, custoDaMorte = 0

    /** tira n cabeças; o que faltar entra antes como encontradas, pelo custo médio */
    const tirar = (n: number): void => {
      if (n > cab) {
        const falta = n - cab
        e.faltaram = falta
        const valorFalta = cab > 0 ? Math.round((custo * falta) / cab) : 0
        const rec = Math.min(falta, sumidos)
        const vRec = falta ? Math.round((valorFalta * rec) / falta) : 0
        sumidos -= rec
        recuperado += vRec
        ganho += valorFalta - vRec
        entrou += valorFalta
        cab += falta
        custo += valorFalta
      }
    }

    switch (m.tipo) {
      case 'entrada':
      case 'ajuste_entrada': {
        if (m.cabecas < 0) tirar(-m.cabecas)
        cab += m.cabecas
        const v = c(m.valor)
        if (v >= 0) entrou += v
        else saiu += -v
        custo += v
        if (custo < 0) { entrou += -custo; custo = 0 } // ajuste maior que o custo: não deixa custo negativo
        // R2D: recria rateada que esperava gado entra na primeira compra que chega
        if (m.cabecas > 0 && semGado > 0) { custo += semGado; entrou += semGado; semGado = 0 }
        break
      }
      case 'recria':
        entrou += c(m.valor)
        custo += c(m.valor)
        break
      case 'morte': {
        const n = -m.cabecas
        tirar(n)
        custoDaMorte = parte(n)
        cab -= n
        // a morte não tira custo: ele se espalha nas que ficaram. Sem nenhuma, vira perda.
        if (cab === 0 && custo > 0) { perdaMorte = custo; saiu += custo; custo = 0 }
        break
      }
      case 'venda':
      case 'ajuste_venda': {
        const n = -m.cabecas // cabeças que saem (ajuste pode devolver: n < 0)
        const receita = c(m.valor)
        const chave = `${m.fichaId ?? ''}|${categoria}`
        if (n > 0) {
          tirar(n)
          if (m.tipo === 'venda' && m.fichaId) medioDaVenda.set(chave, cab ? custo / cab : 0)
          const sai = parte(n)
          cab -= n
          custo -= sai
          saiu += sai
          lucro = receita - sai
        } else if (n < 0) {
          // vendeu menos do que se lançou: as cabeças voltam pelo custo com que saíram
          const volta = Math.round((medioDaVenda.get(chave) ?? (cab ? custo / cab : 0)) * -n)
          cab += -n
          custo += volta
          entrou += volta
          lucro = receita + volta
        } else {
          lucro = receita
        }
        break
      }
      case 'contagem': {
        const alvo = Math.max(0, m.contadas ?? cab)
        const dif = alvo - cab
        if (dif < 0) {
          // sumidos (ou zeragem): saem pelo custo médio, como perda de contagem
          const sai = parte(-dif)
          perda = sai
          saiu += sai
          sumidos += -dif
          custo -= sai
        } else if (dif > 0) {
          // encontradas: entram pelo custo médio; primeiro anulam os sumidos de antes
          const vale = cab > 0 ? Math.round((custo * dif) / cab) : 0
          const rec = Math.min(dif, sumidos)
          const vRec = Math.round((vale * rec) / dif)
          sumidos -= rec
          recuperado += vRec
          ganho += vale - vRec
          entrou += vale
          custo += vale
        }
        cab = alvo
        break
      }
    }

    b.cab = cab
    b.custo = custo
    b.sumidos = sumidos
    e.cabDepois = cab
    e.custoDepois = r(custo)
    e.custoSaiu = r(saiu)
    e.custoEntrou = r(entrou)
    e.lucro = r(lucro)
    e.ganho = r(ganho)
    e.perda = r(perda)
    e.recuperado = r(recuperado)
    e.perdaMorte = r(perdaMorte)
    e.custoDaMorte = r(custoDaMorte)
    efeitos.push(e)
  }

  const categorias: SaldoDaCategoria[] = [...bolsos.entries()].map(([categoria, b]) => ({
    categoria, cabecas: b.cab, custo: r(b.custo), custoMedio: b.cab > 0 ? r(Math.round(b.custo / b.cab)) : null, sumidos: b.sumidos,
  }))
  const cab = totalCab()
  const custo = totalCusto()
  return {
    cabecas: cab, custo: r(custo), custoMedio: cab > 0 ? r(Math.round(custo / cab)) : null,
    sumidos: categorias.reduce((s, x) => s + x.sumidos, 0), efeitos, categorias, semGado: r(semGado),
  }
}

/** R2D · As categorias que o gestor vê: as que têm gado, custo ou sumidos (a vazia de tudo some). */
export function categoriasComAlgo(calc: Pick<Calculo, 'categorias'>): SaldoDaCategoria[] {
  return calc.categorias.filter((x) => x.cabecas !== 0 || x.custo !== 0 || x.sumidos !== 0)
}

/**
 * O estoque logo ANTES de um momento (dia + hora do lançamento): é contra ele
 * que se confere uma venda ou uma morte nova — e se propõe a correção.
 * R2D: com `categoria`, o saldo só daquela categoria.
 */
export function estoqueAntes(movs: Movimento[], data: string, em: string, estoqueId = ESTOQUE_PRINCIPAL, categoria?: string): Saldo {
  const antes = movs.filter((m) => m.data < data || (m.data === data && m.em < em))
  const k = recalcular(antes, estoqueId)
  if (categoria === undefined) return { cabecas: k.cabecas, custo: k.custo, custoMedio: k.custoMedio, sumidos: k.sumidos }
  const s = k.categorias.find((x) => x.categoria === categoria)
  return s ? { cabecas: s.cabecas, custo: s.custo, custoMedio: s.custoMedio, sumidos: s.sumidos } : { cabecas: 0, custo: 0, custoMedio: null, sumidos: 0 }
}

/**
 * R2D · Para onde vai uma categoria da VENDA no estoque: a de mesmo nome
 * (sem diferença de maiúsculas e espaços); se não há, e o estoque tem UMA
 * categoria com gado, ela (quem trabalha com uma categoria só vende «Boi
 * gordo» do estoque «Geral» sem perceber nada); senão, o próprio nome — e o
 * app propõe as «encontradas», como sempre.
 */
export function categoriaNoEstoque(categoria: string, saldos: Pick<SaldoDaCategoria, 'categoria' | 'cabecas'>[]): string {
  const norm = (s: string) => s.trim().toLocaleLowerCase('pt-BR')
  const igual = saldos.find((x) => norm(x.categoria) === norm(categoria))
  if (igual) return igual.categoria
  const comGado = saldos.filter((x) => x.cabecas > 0)
  return comGado.length === 1 ? comGado[0]!.categoria : categoria
}

/** R2D · Junta partes da mesma categoria (ex.: dois lotes de venda que caíram na mesma categoria do estoque). */
export function juntarPorCategoria(partes: ParteDaCategoria[]): ParteDaCategoria[] {
  const out: ParteDaCategoria[] = []
  for (const p of partes) {
    const ja = out.find((x) => x.categoria === p.categoria)
    if (!ja) { out.push({ ...p }); continue }
    ja.cabecas += p.cabecas
    ja.valor = r(c(ja.valor) + c(p.valor))
    ja.kg = ja.kg === null || p.kg === null ? null : Math.round((ja.kg + p.kg) * 100) / 100
    ja.arrobas = ja.arrobas === null || p.arrobas === null ? null : Math.round((ja.arrobas + p.arrobas) * 10000) / 10000
  }
  return out
}

/* ---------- resultados de um período ---------- */

export interface ResultadoDaCategoria {
  categoria: string
  lucroVendas: number
  receitaVendas: number
  custoDasVendas: number
  cabVendidas: number
  cabEntradas: number
  custoEntradas: number
  /** recria desta categoria (a escolhida e a parte dela nas rateadas) */
  gastosRecria: number
  mortes: number
  custoMortes: number
  perdaMorte: number
  /** encontradas − sumidos (com os sumidos que reapareceram) */
  ajusteContagem: number
  resultado: number
}

export interface Resultados {
  /** lucro das vendas (valor − custo médio × cabeças), ajustes de venda incluídos */
  lucroVendas: number
  receitaVendas: number
  custoDasVendas: number
  cabVendidas: number
  /** contagem: encontradas além dos sumidos de antes */
  ganhoContagem: number
  /** contagem: sumidos e zeragem */
  perdaContagem: number
  /** encontradas que anulam sumidos de antes */
  recuperadoContagem: number
  /** R2D · as três de cima numa linha só: ganho + recuperado − perda */
  ajusteContagem: number
  /** o custo que ficou sem gado quando morreu a última cabeça (desconta do resultado) */
  perdaMorte: number
  /** R2D · quanto as cabeças mortas valiam ao custo médio da data (informativo: já está no custo das que ficaram) */
  custoMortes: number
  /** gastos de recria (já estão dentro do custo médio: informativo) */
  gastosRecria: number
  mortes: number
  cabEntradas: number
  custoEntradas: number
  /** R2D · «Custo das entradas» = compras + recria. Não é caixa: é o que entrou no custo do estoque. */
  custoDasEntradas: number
  /** resultado = lucro das vendas + ajuste de contagem − perda por morte (última cabeça) */
  resultado: number
  /**
   * Vendas − compras − recria (contagem não entra). Não aparece mais na tela
   * («não é caixa», gestor, 06/10/2026); fica para as contas antigas.
   */
  caixa: number
  /** R2D · o mesmo, por categoria (recria rateada sem gado não entra em nenhuma) */
  porCategoria: ResultadoDaCategoria[]
}

export function resultadosDoPeriodo(calc: Calculo, de: string, ate: string): Resultados {
  const s = {
    lucroVendas: 0, receitaVendas: 0, custoDasVendas: 0, cabVendidas: 0, ganhoContagem: 0, perdaContagem: 0,
    recuperadoContagem: 0, perdaMorte: 0, custoMortes: 0, gastosRecria: 0, mortes: 0, cabEntradas: 0, custoEntradas: 0,
  }
  const cats = new Map<string, Omit<ResultadoDaCategoria, 'categoria' | 'resultado'>>()
  const daCat = (k: string) => {
    let x = cats.get(k)
    if (!x) {
      x = { lucroVendas: 0, receitaVendas: 0, custoDasVendas: 0, cabVendidas: 0, cabEntradas: 0, custoEntradas: 0, gastosRecria: 0,
        mortes: 0, custoMortes: 0, perdaMorte: 0, ajusteContagem: 0 }
      cats.set(k, x)
    }
    return x
  }
  for (const e of calc.efeitos) {
    if (e.m.data < de || e.m.data > ate) continue
    const t = e.m.tipo
    const k = e.categoria === RATEIO ? null : daCat(e.categoria)
    if (t === 'venda' || t === 'ajuste_venda') {
      const saiu = c(e.custoSaiu) - c(e.custoEntrou)
      s.lucroVendas += c(e.lucro)
      s.receitaVendas += c(e.m.valor)
      s.custoDasVendas += saiu
      s.cabVendidas += -e.m.cabecas
      if (k) { k.lucroVendas += c(e.lucro); k.receitaVendas += c(e.m.valor); k.custoDasVendas += saiu; k.cabVendidas += -e.m.cabecas }
    }
    if (t === 'entrada' || t === 'ajuste_entrada') {
      s.cabEntradas += e.m.cabecas
      s.custoEntradas += c(e.m.valor)
      if (k) { k.cabEntradas += e.m.cabecas; k.custoEntradas += c(e.m.valor) }
    }
    if (t === 'recria') {
      s.gastosRecria += c(e.m.valor)
      if (k) k.gastosRecria += c(e.m.valor)
      for (const p of e.rateio ?? []) daCat(p.categoria).gastosRecria += c(p.valor)
    }
    if (t === 'morte') {
      s.mortes += -e.m.cabecas
      s.custoMortes += c(e.custoDaMorte)
      if (k) { k.mortes += -e.m.cabecas; k.custoMortes += c(e.custoDaMorte) }
    }
    s.ganhoContagem += c(e.ganho)
    s.perdaContagem += c(e.perda)
    s.recuperadoContagem += c(e.recuperado)
    s.perdaMorte += c(e.perdaMorte)
    if (k) { k.ajusteContagem += c(e.ganho) + c(e.recuperado) - c(e.perda); k.perdaMorte += c(e.perdaMorte) }
  }
  const ajusteContagem = s.ganhoContagem + s.recuperadoContagem - s.perdaContagem
  const resultado = s.lucroVendas + ajusteContagem - s.perdaMorte
  const caixa = s.receitaVendas - s.custoEntradas - s.gastosRecria
  const porCategoria: ResultadoDaCategoria[] = [...cats.entries()].map(([categoria, x]) => ({
    categoria,
    lucroVendas: r(x.lucroVendas), receitaVendas: r(x.receitaVendas), custoDasVendas: r(x.custoDasVendas), cabVendidas: x.cabVendidas,
    cabEntradas: x.cabEntradas, custoEntradas: r(x.custoEntradas), gastosRecria: r(x.gastosRecria), mortes: x.mortes,
    custoMortes: r(x.custoMortes), perdaMorte: r(x.perdaMorte), ajusteContagem: r(x.ajusteContagem),
    resultado: r(x.lucroVendas + x.ajusteContagem - x.perdaMorte),
  }))
  return {
    lucroVendas: r(s.lucroVendas), receitaVendas: r(s.receitaVendas), custoDasVendas: r(s.custoDasVendas),
    cabVendidas: s.cabVendidas, ganhoContagem: r(s.ganhoContagem), perdaContagem: r(s.perdaContagem),
    recuperadoContagem: r(s.recuperadoContagem), ajusteContagem: r(ajusteContagem), perdaMorte: r(s.perdaMorte),
    custoMortes: r(s.custoMortes), gastosRecria: r(s.gastosRecria),
    mortes: s.mortes, cabEntradas: s.cabEntradas, custoEntradas: r(s.custoEntradas),
    custoDasEntradas: r(s.custoEntradas + s.gastosRecria),
    resultado: r(resultado), caixa: r(caixa), porCategoria,
  }
}

/* ---------- R2D · peso e arroba por categoria ---------- */

export interface PesosDaCategoria {
  categoria: string
  cabEntradas: number
  /** peso médio na compra (kg por cabeça); nulo se alguma compra da categoria veio sem peso */
  pesoEntrada: number | null
  /** custo de compra por arroba; nulo se alguma compra não foi por arroba */
  custoPorArrobaEntrada: number | null
  cabVendidas: number
  pesoVenda: number | null
  /** peso de venda − peso de compra, por cabeça */
  ganhoPorCabeca: number | null
  /** custo médio que saiu nas vendas (compra + recria) por arroba vendida */
  custoPorArrobaVendida: number | null
  /** valor da venda por arroba vendida */
  vendaPorArroba: number | null
}

/**
 * Peso e arroba de cada categoria, de tudo o que entrou e saiu ATÉ `ate`
 * (a compra de março e a venda de setembro na mesma conta). Só fala de peso
 * quando todas as fichas daquela categoria trouxeram peso — média com animal
 * sem peso não é média de nada.
 */
export function pesosPorCategoria(calc: Calculo, ate = '9999-12-31'): PesosDaCategoria[] {
  type Soma = { cab: number; valor: number; custo: number; kg: number; arrobas: number; semKg: boolean; semArroba: boolean }
  const zero = (): Soma => ({ cab: 0, valor: 0, custo: 0, kg: 0, arrobas: 0, semKg: false, semArroba: false })
  const ent = new Map<string, Soma>(), ven = new Map<string, Soma>()
  const somar = (mapa: Map<string, Soma>, k: string, cab: number, valor: number, custo: number, m: Movimento) => {
    let x = mapa.get(k)
    if (!x) { x = zero(); mapa.set(k, x) }
    x.cab += cab
    x.valor += valor
    x.custo += custo
    // ajuste que só mexe no dinheiro (comissão lançada depois) não precisa de peso
    if (cab !== 0 || m.tipo === 'entrada' || m.tipo === 'venda') {
      if (m.kg === null || m.kg === undefined) x.semKg = true
      else x.kg += m.kg
      if (m.arrobas === null || m.arrobas === undefined) x.semArroba = true
      else x.arrobas += m.arrobas
    }
  }
  for (const e of calc.efeitos) {
    if (e.m.data > ate || e.categoria === RATEIO) continue
    const t = e.m.tipo
    if (t === 'entrada' || t === 'ajuste_entrada') somar(ent, e.categoria, e.m.cabecas, c(e.m.valor), 0, e.m)
    if (t === 'venda' || t === 'ajuste_venda') somar(ven, e.categoria, -e.m.cabecas, c(e.m.valor), c(e.custoSaiu) - c(e.custoEntrou), e.m)
  }
  const nomes = [...new Set([...ent.keys(), ...ven.keys()])]
  const dois = (n: number) => Math.round(n * 100) / 100
  return nomes.map((categoria) => {
    const a = ent.get(categoria) ?? zero(), v = ven.get(categoria) ?? zero()
    const pesoEntrada = a.cab > 0 && !a.semKg && a.kg > 0 ? dois(a.kg / a.cab) : null
    const pesoVenda = v.cab > 0 && !v.semKg && v.kg > 0 ? dois(v.kg / v.cab) : null
    return {
      categoria, cabEntradas: a.cab, pesoEntrada,
      custoPorArrobaEntrada: a.cab > 0 && !a.semArroba && a.arrobas > 0 ? dois(a.valor / 100 / a.arrobas) : null,
      cabVendidas: v.cab, pesoVenda,
      ganhoPorCabeca: pesoEntrada !== null && pesoVenda !== null ? dois(pesoVenda - pesoEntrada) : null,
      custoPorArrobaVendida: v.cab > 0 && !v.semArroba && v.arrobas > 0 ? dois(v.custo / 100 / v.arrobas) : null,
      vendaPorArroba: v.cab > 0 && !v.semArroba && v.arrobas > 0 ? dois(v.valor / 100 / v.arrobas) : null,
    }
  })
}

/* ---------- a ficha e o estoque ---------- */

/**
 * O que uma ficha leva para o estoque AGORA:
 *  - Compra: as cabeças do romaneio e o CUSTO TOTAL da compra com comissão
 *    (vendedor + comissão + frete + gastos — o fechamento da 2E). Comissão
 *    ainda não lançada fica de fora; quando o gestor lançar, a diferença vira
 *    um ajuste de compra, com data;
 *  - Compra com porteiras (3B): só a porteira «Meu estoque», com o custo dela
 *    (src/dominio/destinos.ts). Pôr ou mudar porteira depois de entrar vira
 *    ajuste, como qualquer outra mudança;
 *  - Venda: as cabeças e o líquido do romaneio;
 *  - cancelada ou excluída: nada (o que já entrou sai por ajuste).
 * R2D: é o TOTAL da ficha; por categoria, `alvoPorCategoria` (a soma bate).
 */
export interface AlvoDaFicha { tipo: 'entrada' | 'venda'; cabecas: number; valor: number; comissaoPendente: boolean }

export function alvoDaFicha(f: Ficha, comissao: Pick<Comissao, 'porCabeca' | 'acertoId'> | null): AlvoDaFicha | null {
  if (f.finalidade !== 'Compra' && f.finalidade !== 'Venda') return null
  const tipo = f.finalidade === 'Compra' ? 'entrada' : 'venda'
  if (f.situacao === 'cancelada' || f.situacao === 'excluida') return { tipo, cabecas: 0, valor: 0, comissaoPendente: false }
  const res = calcular(f)
  if (tipo === 'venda') return { tipo, cabecas: res.cab, valor: res.liquido, comissaoPendente: false }
  // 3B: compra com porteiras entra só pela porteira «Meu estoque» (cabeças e
  // custo dela, sem comissão de cliente). Cliente e revenda não são estoque.
  // Sem porteiras, a ficha inteira, como sempre foi.
  const est = estoqueDaFicha(f, comissao)
  if (est) return { tipo, cabecas: est.cabecas, valor: est.valor, comissaoPendente: !comissao && est.cabecas > 0 }
  const fe = fechamentoDaCompra(f, res, { verComissao: true, comissao })
  return { tipo, cabecas: res.cab, valor: fe.total, comissaoPendente: fe.comissaoPendente }
}

/** R2D · O mesmo alvo, separado por categoria (conforme os lotes). A soma das partes é o `alvoDaFicha`, ao centavo. */
export interface AlvoPorCategoria { tipo: 'entrada' | 'venda'; partes: ParteDaCategoria[]; comissaoPendente: boolean }

export function alvoPorCategoria(f: Ficha, comissao: Pick<Comissao, 'porCabeca' | 'acertoId'> | null): AlvoPorCategoria | null {
  if (f.finalidade !== 'Compra' && f.finalidade !== 'Venda') return null
  const tipo = f.finalidade === 'Compra' ? 'entrada' : 'venda'
  if (f.situacao === 'cancelada' || f.situacao === 'excluida') return { tipo, partes: [], comissaoPendente: false }
  const res = calcular(f)
  if (tipo === 'venda') return { tipo, partes: fichaPorCategoria(f, res.liquido), comissaoPendente: false }
  const est = estoquePorCategoria(f, comissao)
  if (est) return { tipo, partes: est, comissaoPendente: !comissao && est.some((p) => p.cabecas > 0) }
  const fe = fechamentoDaCompra(f, res, { verComissao: true, comissao })
  return { tipo, partes: fichaPorCategoria(f, fe.total), comissaoPendente: fe.comissaoPendente }
}

/** O que os movimentos valendo já levaram desta ficha (entrada + ajustes, ou venda + ajustes). */
export function jaLancadoDaFicha(movs: Movimento[], fichaId: string, estoqueId = ESTOQUE_PRINCIPAL):
  { tipo: 'entrada' | 'venda'; cabecas: number; valor: number; principal: Movimento } | null {
  const v = validos(movs, estoqueId).filter((m) => m.fichaId === fichaId)
  const principal = v.find((m) => m.tipo === 'entrada' || m.tipo === 'venda')
  if (!principal) return null
  const tipo = principal.tipo as 'entrada' | 'venda'
  const doTipo = v.filter((m) => m.tipo === tipo || m.tipo === `ajuste_${tipo}`)
  // venda guarda as cabeças negativas (saem); aqui devolve quantas saíram
  const cab = doTipo.reduce((s, m) => s + m.cabecas, 0)
  return { tipo, cabecas: tipo === 'venda' ? -cab : cab, valor: r(doTipo.reduce((s, m) => s + c(m.valor), 0)), principal }
}

/** R2D · O que já foi lançado desta ficha, por categoria (cabeças como a ficha conta: vendidas são positivas). */
export function jaLancadoPorCategoria(movs: Movimento[], fichaId: string, estoqueId = ESTOQUE_PRINCIPAL):
  { tipo: 'entrada' | 'venda'; partes: ParteDaCategoria[] } | null {
  const v = validos(movs, estoqueId).filter((m) => m.fichaId === fichaId)
  const principal = v.find((m) => m.tipo === 'entrada' || m.tipo === 'venda')
  if (!principal) return null
  const tipo = principal.tipo as 'entrada' | 'venda'
  const sinal = tipo === 'venda' ? -1 : 1
  const partes: ParteDaCategoria[] = []
  for (const m of v.filter((x) => x.tipo === tipo || x.tipo === `ajuste_${tipo}`)) {
    const k = categoriaDoMovimento(m)
    let p = partes.find((x) => x.categoria === k)
    if (!p) { p = { categoria: k, cabecas: 0, valor: 0, kg: 0, arrobas: 0 }; partes.push(p) }
    p.cabecas += sinal * m.cabecas
    p.valor = r(c(p.valor) + c(m.valor))
    const pesa = m.cabecas !== 0 || m.tipo === tipo
    if (pesa) {
      p.kg = p.kg === null || m.kg === null || m.kg === undefined ? null : Math.round((p.kg + m.kg) * 100) / 100
      p.arrobas = p.arrobas === null || m.arrobas === null || m.arrobas === undefined ? null : Math.round((p.arrobas + m.arrobas) * 10000) / 10000
    }
  }
  return { tipo, partes }
}

export interface Diferenca { fichaId: string; tipo: 'entrada' | 'venda'; cabecas: number; valor: number }

/**
 * A ficha mudou depois de entrar (ou sair) do estoque: comissão lançada, gasto
 * novo, pesou mais, corrigiu a negociação, cancelou. Devolve a diferença, que
 * vira um movimento de ajuste com a data de hoje — nada muda o passado em
 * silêncio. `cabecas` e `valor` são como a ficha conta (cabeças vendidas, não
 * o sinal do estoque). Ficha aberta (reaberta para pesar mais) espera encerrar.
 * R2D: é a diferença do TOTAL; a tela lança `diferencasPorCategoria`.
 */
export function diferencaDaFicha(movs: Movimento[], f: Pick<Ficha, 'id' | 'situacao'>, alvo: AlvoDaFicha | null): Diferenca | null {
  if (!alvo || f.situacao === 'aberta') return null
  const ja = jaLancadoDaFicha(movs, f.id)
  if (!ja || ja.tipo !== alvo.tipo) return null
  const cab = alvo.cabecas - ja.cabecas
  const valor = r(c(alvo.valor) - c(ja.valor))
  if (!cab && !valor) return null
  return { fichaId: f.id, tipo: alvo.tipo, cabecas: cab, valor }
}

export interface DiferencaDaCategoria extends Diferenca {
  categoria: string
  kg: number | null
  arrobas: number | null
}

/**
 * R2D · A mesma diferença, por categoria. Cada categoria do alvo vai para a
 * categoria em que a ficha já está lançada:
 *  - a de mesmo nome;
 *  - se a ficha foi lançada antes da 015 (tudo em «Geral»), tudo em «Geral» —
 *    ficha antiga não vira «−20 Geral, +20 Boi» só porque o app mudou;
 *  - na venda, se ela saiu numa categoria só (o estoque de uma categoria),
 *    nessa — é a mesma regra com que ela saiu (`categoriaNoEstoque`).
 * Só vira ajuste o que muda cabeças ou dinheiro (o banco não aceita ajuste só
 * de peso); o peso vai junto quando se sabe.
 */
export function diferencasPorCategoria(movs: Movimento[], f: Pick<Ficha, 'id' | 'situacao'>, alvo: AlvoPorCategoria | null): DiferencaDaCategoria[] {
  if (!alvo || f.situacao === 'aberta') return []
  const ja = jaLancadoPorCategoria(movs, f.id)
  if (!ja || ja.tipo !== alvo.tipo) return []
  const lancadas = ja.partes.map((p) => p.categoria)
  const umaSo = lancadas.length === 1 && (lancadas[0] === CATEGORIA_GERAL || alvo.tipo === 'venda') ? lancadas[0]! : null
  const norm = (s: string) => s.trim().toLocaleLowerCase('pt-BR')
  const mapear = (k: string) => lancadas.find((x) => norm(x) === norm(k)) ?? umaSo ?? k
  const quer = juntarPorCategoria(alvo.partes.map((p) => ({ ...p, categoria: mapear(p.categoria) })))
  const nomes = [...new Set([...lancadas, ...quer.map((p) => p.categoria)])]
  const out: DiferencaDaCategoria[] = []
  for (const k of nomes) {
    const a = quer.find((p) => p.categoria === k) ?? { categoria: k, cabecas: 0, valor: 0, kg: 0, arrobas: 0 }
    const j = ja.partes.find((p) => p.categoria === k) ?? { categoria: k, cabecas: 0, valor: 0, kg: 0, arrobas: 0 }
    const cabecas = a.cabecas - j.cabecas
    const valor = r(c(a.valor) - c(j.valor))
    if (!cabecas && !valor) continue
    out.push({
      fichaId: f.id, tipo: alvo.tipo, categoria: k, cabecas, valor,
      kg: a.kg !== null && j.kg !== null ? Math.round((a.kg - j.kg) * 100) / 100 : null,
      arrobas: a.arrobas !== null && j.arrobas !== null ? Math.round((a.arrobas - j.arrobas) * 10000) / 10000 : null,
    })
  }
  return out
}

/* ---------- planilha ---------- */

const doisDecimais = (n: number | null) => (n === null || !Number.isFinite(n) ? null : Math.round(n * 100) / 100)

/** A catira em Excel: o resumo do período (total e por categoria) e os movimentos com o saldo depois de cada um. */
export function abasDaCatira(calc: Calculo, de: string, ate: string, numeroDaFicha: (id: string) => string): Aba[] {
  const res = resultadosDoPeriodo(calc, de, ate)
  const negritoR: number[] = [0]
  const R: Celula[][] = [
    [`Meu estoque — de ${dataBR(de)} a ${dataBR(ate)}`],
    [],
    ['Estoque hoje', 'Cabeças', calc.cabecas, 'Custo total (R$)', calc.custo, 'Custo médio (R$/cab)', doisDecimais(calc.custoMedio)],
  ]
  const cats = categoriasComAlgo(calc)
  if (cats.length > 1 || (cats.length === 1 && cats[0]!.categoria !== CATEGORIA_GERAL)) {
    for (const x of cats) R.push([`   ${x.categoria}`, 'Cabeças', x.cabecas, 'Custo total (R$)', x.custo, 'Custo médio (R$/cab)', doisDecimais(x.custoMedio)])
  }
  R.push([])
  negritoR.push(R.length)
  R.push(['RESULTADO DO PERÍODO', 'R$'])
  R.push(['Lucro das vendas', res.lucroVendas])
  R.push(['   vendas (líquido dos romaneios)', res.receitaVendas])
  R.push(['   custo médio das cabeças vendidas', -res.custoDasVendas])
  R.push(['Ajuste de contagem', res.ajusteContagem])
  R.push(['   cabeças encontradas', res.ganhoContagem])
  R.push(['   sumidos que apareceram', res.recuperadoContagem])
  R.push(['   sumidos e zeragem', -res.perdaContagem])
  R.push([`Perda por morte: ${res.mortes} ${res.mortes === 1 ? 'cabeça' : 'cabeças'}, ao custo médio da data`, res.custoMortes])
  R.push(['   já espalhada no custo das que ficaram (não desconta de novo)', null])
  R.push(['   morte da última cabeça da categoria (desconta)', -res.perdaMorte])
  negritoR.push(R.length)
  R.push(['Resultado', res.resultado])
  R.push(['Gastos de recria (já dentro do custo médio)', res.gastosRecria])
  R.push([])
  negritoR.push(R.length)
  R.push(['CUSTO DAS ENTRADAS (não é caixa)', 'R$'])
  R.push(['Compras (custo total)', res.custoEntradas])
  R.push(['Recria', res.gastosRecria])
  negritoR.push(R.length)
  R.push(['Custo das entradas', res.custoDasEntradas])
  R.push([])
  R.push(['Cabeças', 'Entraram', res.cabEntradas, 'Vendidas', res.cabVendidas, 'Mortes', res.mortes])
  if (res.porCategoria.length > 1 || (res.porCategoria[0] && res.porCategoria[0].categoria !== CATEGORIA_GERAL)) {
    R.push([])
    negritoR.push(R.length)
    R.push(['POR CATEGORIA', 'Entraram (cab)', 'Vendidas (cab)', 'Lucro das vendas (R$)', 'Ajuste de contagem (R$)', 'Mortes (cab)',
      'Perda por morte ao custo médio (R$)', 'Resultado (R$)'])
    for (const x of res.porCategoria) {
      R.push([x.categoria, x.cabEntradas, x.cabVendidas, x.lucroVendas, x.ajusteContagem, x.mortes, x.custoMortes, x.resultado])
    }
  }
  const pesos = pesosPorCategoria(calc, ate)
  if (pesos.some((p) => p.pesoEntrada !== null || p.custoPorArrobaEntrada !== null || p.pesoVenda !== null)) {
    R.push([])
    negritoR.push(R.length)
    R.push([`PESO E ARROBA (tudo até ${dataBR(ate)})`, 'Peso de compra (kg/cab)', 'Custo de compra (R$/@)', 'Peso de venda (kg/cab)',
      'Ganho de peso (kg/cab)', 'Custo das vendidas (R$/@)', 'Venda (R$/@)'])
    for (const p of pesos) {
      R.push([p.categoria, p.pesoEntrada, p.custoPorArrobaEntrada, p.pesoVenda, p.ganhoPorCabeca, p.custoPorArrobaVendida, p.vendaPorArroba])
    }
  }
  const M: Celula[][] = [['Data', 'Tipo', 'Categoria', 'Ficha', 'Gasto', 'Descrição', 'Cabeças', 'Contadas', 'Kg', 'Arrobas', 'Valor (R$)',
    'Custo que saiu (R$)', 'Lucro (R$)', 'Ajuste de contagem (R$)', 'Cabeças depois', 'Custo depois (R$)', 'Custo médio depois (R$)']]
  for (const e of calc.efeitos) {
    if (e.m.data < de || e.m.data > ate) continue
    const venda = e.m.tipo === 'venda' || e.m.tipo === 'ajuste_venda'
    const cat = e.categoria === RATEIO
      ? `Rateada: ${(e.rateio ?? []).map((p) => `${p.categoria} ${p.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`).join('; ') || 'sem gado (vai para a próxima compra)'}`
      : e.categoria
    M.push([dataBR(e.m.data), NOME_DO_TIPO[e.m.tipo], cat, e.m.fichaId ? numeroDaFicha(e.m.fichaId) : '', e.m.categoria, e.m.descricao,
      e.m.tipo === 'contagem' ? e.cabDepois - e.cabAntes : e.m.cabecas, e.m.contadas, e.m.kg ?? null, e.m.arrobas ?? null, e.m.valor || null,
      e.custoSaiu || null, venda ? e.lucro : null, doisDecimais(e.ganho + e.recuperado - e.perda) || null,
      e.cabDepois, e.custoDepois, e.cabDepois ? doisDecimais(e.custoDepois / e.cabDepois) : null])
  }
  return [
    { nome: 'Resumo', linhas: R, larguras: [52, 16, 16, 18, 18, 20, 20, 14], negrito: negritoR },
    { nome: 'Movimentos', linhas: M, larguras: [11, 18, 22, 14, 12, 30, 9, 9, 10, 10, 13, 14, 12, 16, 10, 14, 14], negrito: [0] },
  ]
}
